import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Loader2, Settings, CheckCircle2, Info } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001/api';

const SettingsPage = () => {
  const [schedulerInfo, setSchedulerInfo] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMode, setSelectedMode] = useState('apscheduler');

  useEffect(() => {
    loadSchedulerInfo();
  }, []);

  const loadSchedulerInfo = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');

      // Get scheduler info
      const infoRes = await axios.get(`${API_BASE_URL}/settings/scheduler`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchedulerInfo(infoRes.data);
      setSelectedMode(infoRes.data.current_mode);

      // Get scheduler status
      const statusRes = await axios.get(`${API_BASE_URL}/settings/scheduler/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchedulerStatus(statusRes.data);
    } catch (error) {
      console.error('Error loading scheduler info:', error);
      toast.error('Failed to load scheduler information');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      const token = localStorage.getItem('authToken');

      const response = await axios.post(
        `${API_BASE_URL}/settings/scheduler`,
        { scheduler_mode: selectedMode },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Scheduler settings updated!');
      toast.info(response.data.action_required);
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="mb-8 flex items-center gap-3">
        <Settings className="w-8 h-8" />
        <div>
          <h1 className="text-3xl font-bold">Settings</h1>
          <p className="text-gray-600">Configure system settings</p>
        </div>
      </div>

      {/* Current Status */}
      {schedulerStatus && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Current Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm text-gray-600">Scheduler Mode</Label>
                <div className="font-semibold capitalize mt-1">{schedulerStatus.mode}</div>
              </div>
              <div>
                <Label className="text-sm text-gray-600">Status</Label>
                <div className="mt-1">
                  {schedulerStatus.is_running ? (
                    <Badge className="bg-green-100 text-green-800">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Running
                    </Badge>
                  ) : (
                    <Badge className="bg-red-100 text-red-800">Stopped</Badge>
                  )}
                </div>
              </div>
              <div>
                <Label className="text-sm text-gray-600">Scheduler Type</Label>
                <div className="font-semibold capitalize mt-1">{schedulerStatus.scheduler_type}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scheduler Configuration */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Scheduler Configuration</CardTitle>
          <CardDescription>
            Choose between APScheduler (lightweight) or Celery (production-ready)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {schedulerInfo && (
            <div className="space-y-6">
              {/* Current Mode Info */}
              <Alert>
                <Info className="w-4 h-4" />
                <AlertDescription>
                  <strong>Current Mode:</strong> {schedulerInfo.current_mode}
                  <br />
                  {schedulerInfo.description}
                </AlertDescription>
              </Alert>

              {/* Mode Selection */}
              <RadioGroup value={selectedMode} onValueChange={setSelectedMode}>
                <div className="space-y-4">
                  {/* APScheduler Option */}
                  <Card className={selectedMode === 'apscheduler' ? 'border-blue-500 border-2' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <RadioGroupItem value="apscheduler" id="apscheduler" className="mt-1" />
                        <div className="flex-1">
                          <Label htmlFor="apscheduler" className="text-lg font-semibold cursor-pointer">
                            APScheduler (Default)
                          </Label>
                          <p className="text-sm text-gray-600 mt-1">
                            Lightweight in-process scheduler. Perfect for small to medium scale deployments.
                          </p>
                          <div className="mt-3 space-y-1">
                            <div className="text-sm">
                              <strong>✅ Benefits:</strong>
                            </div>
                            <ul className="text-sm text-gray-600 list-disc list-inside ml-2">
                              <li>Easy to set up</li>
                              <li>No external dependencies</li>
                              <li>Perfect for small to medium scale</li>
                              <li>Lower operational complexity</li>
                            </ul>
                          </div>
                          <div className="mt-2">
                            <Badge variant="outline">Recommended for most users</Badge>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Celery Option */}
                  <Card className={selectedMode === 'celery' ? 'border-blue-500 border-2' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <RadioGroupItem value="celery" id="celery" className="mt-1" />
                        <div className="flex-1">
                          <Label htmlFor="celery" className="text-lg font-semibold cursor-pointer">
                            Celery + Redis (Production)
                          </Label>
                          <p className="text-sm text-gray-600 mt-1">
                            Distributed task queue with Redis. Ideal for high-volume production environments.
                          </p>
                          <div className="mt-3 space-y-1">
                            <div className="text-sm">
                              <strong>✅ Benefits:</strong>
                            </div>
                            <ul className="text-sm text-gray-600 list-disc list-inside ml-2">
                              <li>Horizontal scalability</li>
                              <li>Distributed task execution</li>
                              <li>Better fault tolerance</li>
                              <li>Task retry and monitoring</li>
                              <li>Suitable for high-volume production</li>
                            </ul>
                          </div>
                          <div className="mt-3 space-y-1">
                            <div className="text-sm">
                              <strong>📋 Requirements:</strong>
                            </div>
                            <ul className="text-sm text-gray-600 list-disc list-inside ml-2">
                              <li>Redis server required</li>
                              <li>Celery workers must run separately</li>
                              <li>Celery beat for periodic tasks</li>
                            </ul>
                          </div>
                          <div className="mt-2">
                            <Badge variant="outline">For enterprise deployments</Badge>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </RadioGroup>

              {/* Save Button */}
              <div className="flex items-center gap-3 pt-4">
                <Button 
                  onClick={handleSaveSettings}
                  disabled={selectedMode === schedulerInfo.current_mode}
                >
                  Save Settings
                </Button>
                {selectedMode !== schedulerInfo.current_mode && (
                  <Alert className="flex-1">
                    <AlertDescription>
                      <strong>Note:</strong> {schedulerInfo.switch_instructions}
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              {/* Current Benefits */}
              {schedulerInfo.current_mode === selectedMode && (
                <Alert>
                  <Info className="w-4 h-4" />
                  <AlertDescription>
                    <strong>Current Benefits:</strong>
                    <ul className="list-disc list-inside ml-2 mt-2">
                      {schedulerInfo.benefits.map((benefit, idx) => (
                        <li key={idx}>{benefit}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Documentation */}
      <Card>
        <CardHeader>
          <CardTitle>Documentation</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 mb-4">
            For detailed setup instructions and troubleshooting, please refer to:
          </p>
          <ul className="text-sm text-gray-600 space-y-2">
            <li>
              <a href="/docs/SCHEDULER_SETUP.md" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                📄 Complete Scheduler Setup Guide
              </a>
            </li>
            <li>
              <a href="/docs/ARCHITECTURE.md" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                📄 System Architecture Documentation
              </a>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
